const baseUrl = process.env.REACT_APP_SERVER_URI ?? 'http://localhost:4000';
const normalizedBaseUrl = baseUrl.replace(/\/$/, '');
const AUTH_STORAGE_KEY = 'pninit-user-session';

const buildUrl = (path) => `${normalizedBaseUrl}${path.startsWith('/') ? path : `/${path}`}`;

const requestJson = async (path, options = {}) => {
  const response = await fetch(buildUrl(path), options);
  const payload = await response.json().catch(() => ({}));

  if (!response.ok || !payload.success) {
    const error = new Error(payload.message ?? `Request failed with status ${response.status}`);
    error.statusCode = response.status;
    throw error;
  }

  return payload;
};

export const registerUser = async (userData, signal) => {
  return requestJson('/api/users/register', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(userData),
    signal,
  });
};

export const loginUser = async (credentials, signal) => {
  return requestJson('/api/users/login', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(credentials),
    signal,
  });
};

export const logoutUser = async (token, signal) => {
  if (!token) {
    return null;
  }

  const response = await fetch(buildUrl('/api/users/logout'), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    signal,
  });

  if (response.status === 204) {
    return null;
  }

  const payload = await response.json().catch(() => ({}));

  if (!response.ok || !payload.success) {
    const error = new Error(payload.message ?? `Request failed with status ${response.status}`);
    error.statusCode = response.status;
    throw error;
  }

  return payload;
};

export const getCurrentUser = async (token, signal) => {
  if (!token) {
    throw new Error('Session token is required.');
  }

  return requestJson('/api/users/me', {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    signal,
  });
};

export const persistAuthSession = (authPayload) => {
  if (typeof window === 'undefined' || !authPayload?.session?.token || !authPayload?.data) {
    return;
  }

  window.localStorage.setItem(
    AUTH_STORAGE_KEY,
    JSON.stringify({
      session: authPayload.session,
      data: authPayload.data,
    })
  );
};

export const readStoredAuthSession = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const rawValue = window.localStorage.getItem(AUTH_STORAGE_KEY);
    return rawValue ? JSON.parse(rawValue) : null;
  } catch (error) {
    window.localStorage.removeItem(AUTH_STORAGE_KEY);
    return null;
  }
};

export const clearStoredAuthSession = () => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.removeItem(AUTH_STORAGE_KEY);
};

export const getUserDisplayName = (user) => {
  const safeUser = user && typeof user === 'object' ? user : {};
  const fullName = [safeUser.firstName, safeUser.lastName].filter(Boolean).join(' ').trim();
  return fullName || safeUser.login || safeUser.email || 'Account';
};

export const getUserInitials = (user) => {
  const safeUser = user && typeof user === 'object' ? user : {};
  const baseValue = String(
    safeUser.login || safeUser.firstName || safeUser.fullName || safeUser.email || 'AC'
  );
  const alphanumeric = baseValue.replace(/[^a-z0-9]/gi, '');
  return (alphanumeric.slice(0, 2) || 'AC').toUpperCase();
};

export const getUserAvatarUrl = (user) => {
  if (!user?.avatarUrl) {
    return '';
  }

  if (/^https?:\/\//i.test(user.avatarUrl)) {
    return user.avatarUrl;
  }

  const path = user.avatarUrl.startsWith('/') ? user.avatarUrl : `/${user.avatarUrl}`;
  return `${normalizedBaseUrl}${path}`;
};

export const updateUserPreferences = async (userId, preferences, signal) => {
  const token = (() => {
    try {
      return JSON.parse(window.localStorage.getItem(AUTH_STORAGE_KEY) || 'null')?.session?.token || '';
    } catch {
      return '';
    }
  })();

  return requestJson(`/api/users/${encodeURIComponent(userId)}/preferences`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(preferences),
    signal,
  });
};

const userService = {
  registerUser,
  loginUser,
  logoutUser,
  getCurrentUser,
  persistAuthSession,
  readStoredAuthSession,
  clearStoredAuthSession,
  getUserDisplayName,
  getUserInitials,
  getUserAvatarUrl,
  updateUserPreferences,
};

export default userService;
